
import React, { useRef, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import TableOfContents from './TableOfContents';
import LegalFooter from './LegalFooter';
import { generateIndustryServiceSchema } from '@/utils/seoSchema';
import LegalCompanyInfo from './LegalCompanyInfo';
import LegalServices from './LegalServices';
import LegalPaymentMethods from './LegalPaymentMethods';
import LegalDisclaimer from './LegalDisclaimer';

const LegalContent = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  // Create refs for each section for smooth scrolling
  const contactRef = useRef<HTMLHeadingElement>(null);
  const companyDetailsRef = useRef<HTMLHeadingElement>(null);
  const vatInfoRef = useRef<HTMLHeadingElement>(null);
  const representativeRef = useRef<HTMLHeadingElement>(null);
  const legalNoticesRef = useRef<HTMLHeadingElement>(null);
  const disclaimerRef = useRef<HTMLHeadingElement>(null);
  const copyrightRef = useRef<HTMLHeadingElement>(null);
  const servicesRef = useRef<HTMLHeadingElement>(null);
  const paymentRef = useRef<HTMLHeadingElement>(null);

  // Function to scroll to section
  const scrollToSection = (sectionRef: React.RefObject<HTMLHeadingElement>) => {
    if (sectionRef.current) {
      // Scroll with offset for header
      const yOffset = -100; 
      const y = sectionRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
      
      // Focus on the heading for accessibility
      sectionRef.current.focus();
    }
  };

  // Back to top functionality
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Focus on first heading for accessibility
    if (document.querySelector('h1')) {
      (document.querySelector('h1') as HTMLElement).focus();
    }
  };

  // Table of contents items - added payment methods section
  const tocItems = [
    {
      id: 'contact-info',
      title: t.contactInfo,
      onClick: () => scrollToSection(contactRef)
    },
    {
      id: 'company-details',
      title: t.companyDetails,
      onClick: () => scrollToSection(companyDetailsRef)
    },
    {
      id: 'vat-info',
      title: t.vatInfo,
      onClick: () => scrollToSection(vatInfoRef)
    },
    {
      id: 'authorized-representative',
      title: t.authorizedRepresentative,
      onClick: () => scrollToSection(representativeRef)
    },
    {
      id: 'our-services',
      title: currentLanguage === 'de' ? 'Unsere Leistungen' :
             currentLanguage === 'en' ? 'Our Services' :
             currentLanguage === 'fr' ? 'Nos Services' : 'I Nostri Servizi',
      onClick: () => scrollToSection(servicesRef)
    },
    {
      id: 'payment-methods',
      title: currentLanguage === 'de' ? 'Zahlungsmethoden' : 
             currentLanguage === 'en' ? 'Payment Methods' :
             currentLanguage === 'fr' ? 'Méthodes de Paiement' : 'Metodi di Pagamento',
      onClick: () => scrollToSection(paymentRef)
    },
    {
      id: 'legal-notices',
      title: t.legalNotices,
      onClick: () => scrollToSection(legalNoticesRef)
    },
    {
      id: 'disclaimer',
      title: t.disclaimer,
      onClick: () => scrollToSection(disclaimerRef)
    },
    {
      id: 'copyright',
      title: t.copyright,
      onClick: () => scrollToSection(copyrightRef)
    }
  ];

  // Add structured data for this page
  useEffect(() => {
    // Get page path based on language
    const pagePath = currentLanguage === 'de' ? '/impressum' : 
                    currentLanguage === 'en' ? '/legal' : 
                    currentLanguage === 'fr' ? '/mentions-legales' : '/note-legali';
                    
    // Generate service schema for the legal page
    generateIndustryServiceSchema(
      'general',
      t.legalTitle + " | Future Level",
      t.legalDescription,
      "https://future-level.ch/opengraph-image-p98pqg.png",
      `https://future-level.ch/${currentLanguage}${pagePath}`,
      currentLanguage
    );
    
    // Cleanup on unmount
    return () => {
      const script = document.getElementById('industry-service-schema');
      if (script) {
        script.remove();
      }
    };
  }, [currentLanguage, t]);
  
  return (
    <div className="p-6 md:p-8 lg:p-10 text-white">
      <div className="max-w-4xl mx-auto">
        <h1 
          className="text-2xl md:text-3xl font-future tracking-wide font-bold mb-6 md:mb-8 text-purple-100 pb-4 border-b border-purple-500/30" 
          tabIndex={0}
        >
          {t.legalTitle}
        </h1>
        
        <TableOfContents items={tocItems} />
        
        <div className="space-y-10 md:space-y-16 text-gray-300">
          <LegalCompanyInfo 
            contactRef={contactRef}
            companyDetailsRef={companyDetailsRef}
            vatInfoRef={vatInfoRef}
            representativeRef={representativeRef}
          />

          <LegalServices servicesRef={servicesRef} />
          
          <LegalPaymentMethods paymentRef={paymentRef} />
          
          <LegalDisclaimer 
            legalNoticesRef={legalNoticesRef}
            disclaimerRef={disclaimerRef}
            copyrightRef={copyrightRef}
          />
        </div>
        
        <LegalFooter onBackToTop={scrollToTop} />
      </div>
    </div>
  );
};

export default LegalContent;
